import { Inject, Injectable } from '@angular/core';
import {
  DevwareMetaDataService,
  DwAction,
  DwActionHandler,
  DwActionResult,
  DwActionType,
  DwMdFormActionHandler,
  DwMdFormActionState,
  DwMetaDataGridActionState,
  DwOrmDataService,
  DwOrmDataServiceToken,
  DwQueryHelper,
  DwSecurityEmulationService,
  DwUserEntity
} from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { AppPermissions } from '../../../meta-data/app-permissions.enum';

interface EmulateUserContextActionConfig {
  userIdField?: string;
}

@Injectable()
export class EmulateUserContextActionBase {
  constructor(
    @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
    private securityEmulationService: DwSecurityEmulationService
  ) { }

  protected emulateFromRecord(record: any, actionConfig?: EmulateUserContextActionConfig): Observable<DwActionResult> {
    const result: DwActionResult = {
      success: false,
    };

    const userId = this.resolveUserId(record, actionConfig);

    if (!userId) {
      result.message = 'User id was not found in the current data context';
      return of(result);
    }

    return this.getUser(userId)
      .pipe(mergeMap(user => {
        if (!user?.UserId) {
          result.message = 'User not found';
          return of(result);
        }

        const displayName = `${user.FirstName || ''} ${user.LastName || ''}`.trim() || user.UserName;

        return this.securityEmulationService.emulateUser(user.UserName, displayName)
          .pipe(map(securityResult => {
            result.success = securityResult.success;
            result.message = securityResult.message;
            return result;
          }));
      }));
  }

  private getUser(userId: number): Observable<DwUserEntity> {
    const md = new DevwareMetaDataService();
    const qh = new DwQueryHelper();
    const query = md.DwUser.CreateQuery();

    query.SetFieldSettings(false, true);
    query.AddFilterAnd(qh.filters.Equal(md.DwUser.UserId, userId));

    return this.dwOrmDataService.executeQuerySingle(query);
  }

  private resolveUserId(record: any, actionConfig?: EmulateUserContextActionConfig): number | null {
    const candidateFields = [
      actionConfig?.userIdField,
      'UserId',
      'userId'
    ].filter((fieldName): fieldName is string => !!fieldName);

    for (const fieldName of candidateFields) {
      const value = this.getValueByPath(record, fieldName);
      const parsedValue = this.toNumber(value);

      if (parsedValue) {
        return parsedValue;
      }
    }

    return null;
  }

  private getValueByPath(data: any, fieldPath: string): any {
    if (!data || !fieldPath) {
      return null;
    }

    if (Object.prototype.hasOwnProperty.call(data, fieldPath)) {
      return data[fieldPath];
    }

    return fieldPath.split('.')
      .reduce((current, key) => current?.[key], data);
  }

  private toNumber(value: any): number | null {
    if (value == null || value === '') {
      return null;
    }

    const parsedValue = typeof value === 'number' ? value : parseInt(value, 10);

    return Number.isFinite(parsedValue) && parsedValue > 0
      ? parsedValue
      : null;
  }
}

@DwAction({
  keyName: 'emulate-user-context-grid-action',
  display: 'Emulate User',
  actionType: DwActionType.gridRowAction,
  isGlobal: true,
  defaultSecurityConfig: {
    requireAuthentication: true,
    useEmulatedPermissions: false,
    permissionMeaning: AppPermissions.entSecurityCanEmulate
  }
})
@Injectable()
export class EmulateUserContextGridActionService extends EmulateUserContextActionBase implements DwActionHandler {
  handleAction(actionState: DwMetaDataGridActionState<EmulateUserContextActionConfig>): Observable<DwActionResult> {
    return this.emulateFromRecord(actionState.data, actionState.actionPreference?.additionalActionConfig);
  }
}

@DwAction({
  keyName: 'emulate-user-context-form-action',
  display: 'Emulate User',
  actionType: DwActionType.formAction,
  isGlobal: true,
  defaultSecurityConfig: {
    requireAuthentication: true,
    useEmulatedPermissions: false,
    permissionMeaning: AppPermissions.entSecurityCanEmulate
  },
  defaultButtonConfig: {
    useButtonDefaults: true,
    useParentDisplay: false,
    buttonText: 'Emulate User',
    iconClass: 'fa fa-user-secret'
  }
})
@Injectable()
export class EmulateUserContextFormActionService extends EmulateUserContextActionBase implements DwMdFormActionHandler {
  handleAction(actionState: DwMdFormActionState<EmulateUserContextActionConfig>): Observable<DwActionResult> {
    return this.emulateFromRecord(actionState.formApi.getFormData<any>(), actionState.actionPreference?.additionalActionConfig);
  }
}
